import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import StatusBadge from './components/StatusBadge';

const VersionHistory = () => {
  const navigate = useNavigate();
  const { documentType } = useParams();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState({ v1: '', v2: '' });
  const [diff, setDiff] = useState(null);

  const fetchVersions = useCallback(async () => {
    try {
      const response = await api.get(`/api/super-admin/legal-documents/version-history/${documentType}`);
      setVersions(response.data.data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      alert('Failed to fetch version history');
    } finally {
      setLoading(false);
    }
  }, [documentType]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions, documentType]);

  const compareVersions = async () => {
    if (!selectedVersions.v1 || !selectedVersions.v2) {
      alert('Please select two versions to compare');
      return;
    }
    
    try {
      const response = await api.get(
        `/api/super-admin/legal-documents/diff/${documentType}/${selectedVersions.v1}/${selectedVersions.v2}`
      );
      setDiff(response.data.data);
    } catch (error) {
      console.error('Error comparing versions:', error);
      alert('Failed to compare versions');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      refund_policy: 'Refund Policy',
      cookie_policy: 'Cookie Policy',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-sm font-medium text-gray-500">Loading version history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200">
          <div>
            <button
              onClick={() => navigate('/super-admin/legal-documents')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 mb-2 focus:outline-none"
            >
              &larr; Back to Legal Management
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              Version History
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
              Document Scope: <span className="text-gray-900 font-semibold">{getDocumentTypeLabel(documentType)}</span>
            </p>
          </div>

          <div className="bg-slate-900 text-white text-xs font-mono px-3 py-1.5 rounded-lg border border-slate-800 w-fit">
            Total Revisions: {versions.length}
          </div>
        </div>

        {/* Compare Tool Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-slate-900 text-white border-b border-slate-800 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-indigo-300">
              Diff & Comparison Tool
            </div>
            <span className="text-xs text-slate-400">Select two revisions to evaluate metrics</span>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
              <div className="sm:col-span-5">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Baseline Version (V1)
                </label>
                <select
                  value={selectedVersions.v1}
                  onChange={(e) => setSelectedVersions({ ...selectedVersions, v1: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                >
                  <option value="">Select Baseline Version</option>
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version} — [{v.status}]
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-5">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Target Version (V2)
                </label>
                <select
                  value={selectedVersions.v2}
                  onChange={(e) => setSelectedVersions({ ...selectedVersions, v2: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                >
                  <option value="">Select Target Version</option>
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version} — [{v.status}]
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <button
                  onClick={compareVersions}
                  className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-xs"
                >
                  Compare
                </button>
              </div>
            </div>

            {/* Comparison Diff Results */}
            {diff && (
              <div className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                    Comparison Analysis Results
                  </h4>
                  <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                    Diff v{diff.version1.version} ➔ v{diff.version2.version}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                  <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-xs">
                    <div className="font-bold text-gray-900">Baseline (v{diff.version1.version})</div>
                    <div className="text-gray-500 mt-1">Effective Date: {formatDate(diff.version1.effectiveDate)}</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-xs">
                    <div className="font-bold text-gray-900">Target (v{diff.version2.version})</div>
                    <div className="text-gray-500 mt-1">Effective Date: {formatDate(diff.version2.effectiveDate)}</div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg border border-gray-200 text-sm space-y-2">
                  <div className="flex justify-between items-center text-xs sm:text-sm py-1 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Document Title Alteration</span>
                    <span className={`font-semibold ${diff.changes.titleChanged ? 'text-amber-600' : 'text-gray-900'}`}>
                      {diff.changes.titleChanged ? 'Title Modified' : 'Unchanged'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs sm:text-sm py-1 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Character Variance</span>
                    <span className={`font-mono font-semibold ${diff.changes.contentLengthDiff > 0 ? 'text-emerald-600' : diff.changes.contentLengthDiff < 0 ? 'text-rose-600' : 'text-gray-700'}`}>
                      {diff.changes.contentLengthDiff > 0 ? `+${diff.changes.contentLengthDiff}` : diff.changes.contentLengthDiff} chars
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs sm:text-sm py-1">
                    <span className="text-gray-600 font-medium">Word Count Variance</span>
                    <span className={`font-mono font-semibold ${diff.changes.wordCountDiff > 0 ? 'text-emerald-600' : diff.changes.wordCountDiff < 0 ? 'text-rose-600' : 'text-gray-700'}`}>
                      {diff.changes.wordCountDiff > 0 ? `+${diff.changes.wordCountDiff}` : diff.changes.wordCountDiff} words
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* All Versions List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-slate-900 text-white border-b border-slate-800 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-300">
              Revision Log History
            </h3>
            <span className="text-xs text-slate-400">Chronological List</span>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              {versions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No version history records found for this document type.
                </div>
              ) : (
                versions.map((version) => (
                  <div 
                    key={version._id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors gap-4"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-base font-bold text-gray-900">
                          v{version.version}
                        </span>
                        <StatusBadge status={version.status} />
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed font-normal">
                        {version.changeLog || 'No revision log commentary provided.'}
                      </p>

                      <div className="text-xs text-gray-400 font-medium pt-0.5">
                        Effective Date: {version.effectiveDate ? formatDate(version.effectiveDate) : 'Not set'}
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/super-admin/legal-documents/details/${version._id}`)}
                      className="w-full sm:w-auto px-4 py-2 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 text-center"
                    >
                      View Details
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default VersionHistory;